import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AuthProvider } from './contexts/AuthContext';
import { WorkspaceProvider } from './contexts/WorkspaceContext';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import AutoLogin from './pages/AutoLogin';
import Workspaces from './pages/Workspaces';
import Builders from './pages/Builders';
import BuilderDetails from './pages/BuilderDetails';
import Projects from './pages/Projects';
import ProjectDetails from './pages/ProjectDetails';
import Subdivisions from './pages/Subdivisions';
import Models from './pages/Models';
import Units from './pages/Units';
import CommonAreas from './pages/CommonAreas';
import Parking from './pages/Parking';
import Lockers from './pages/Lockers';
import Bicycles from './pages/Bicycles';
import Contacts from './pages/Contacts';
import Deals from './pages/Deals';
import ColorCollections from './pages/ColorCollections';
import PackageLocations from './pages/PackageLocations';
import Packages from './pages/Packages';
import Options from './pages/Options';
import ServiceDashboard from './pages/ServiceDashboard';
import ServiceDocuments from './pages/ServiceDocuments';
import Tarion from './pages/Tarion';
import Deficiencies from './pages/Deficiencies';
import ConstructionStatus from './pages/ConstructionStatus';
import Inspection from './pages/Inspection';
import InspectionWalkthrough from './pages/InspectionWalkthrough';
import Checklist from './pages/Checklist';
import ServiceSettings from './pages/ServiceSettings';
import Users from './pages/Users';
import Roles from './pages/Roles';
import Settings from './pages/Settings';
import ProjectProgress from './pages/ProjectProgress';
import HomeOwners from './pages/HomeOwners';
import UnitDeal from './pages/UnitDeal';
import Customization from './pages/Customization';
import PortalAdmins from './pages/PortalAdmins';
import PortalSettings from './pages/PortalSettings';
import ReportsDashboard from './pages/ReportsDashboard';
import ReportsFinance from './pages/ReportsFinance';
import ReportsSuiteLayouts from './pages/ReportsSuiteLayouts';
import ReportsConstruction from './pages/ReportsConstruction';
import ReportsParking from './pages/ReportsParking';
import ReportsUnits from './pages/ReportsUnits';

const App: React.FC = () => {
  return (
    <BrowserRouter>
      <AuthProvider>
        <WorkspaceProvider>
          <ToastContainer
            position="top-right"
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop={true}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="light"
          />
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/auto-login" element={<AutoLogin />} />

            {/* Protected Routes */}
            <Route
              path="/workspaces"
              element={
                <ProtectedRoute>
                  <Workspaces />
                </ProtectedRoute>
              }
            />

            {/* Reports Routes */}
            <Route
              path="/workspaces/:workspaceId/reports/dashboard"
              element={
                <ProtectedRoute>
                  <ReportsDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/workspaces/:workspaceId/reports/finance"
              element={
                <ProtectedRoute>
                  <ReportsFinance />
                </ProtectedRoute>
              }
            />
            <Route
              path="/workspaces/:workspaceId/reports/suite-layouts"
              element={
                <ProtectedRoute>
                  <ReportsSuiteLayouts />
                </ProtectedRoute>
              }
            />
            <Route
              path="/workspaces/:workspaceId/reports/construction"
              element={
                <ProtectedRoute>
                  <ReportsConstruction />
                </ProtectedRoute>
              }
            />
            <Route
              path="/workspaces/:workspaceId/reports/parking"
              element={
                <ProtectedRoute>
                  <ReportsParking />
                </ProtectedRoute>
              }
            />
            <Route
              path="/workspaces/:workspaceId/reports/units"
              element={
                <ProtectedRoute>
                  <ReportsUnits />
                </ProtectedRoute>
              }
            />

            <Route
              path="/workspaces/:workspaceId/builders"
              element={
                <ProtectedRoute>
                  <Builders />
                </ProtectedRoute>
              }
            />
            <Route
              path="/workspaces/:workspaceId/builders/:builderId"
              element={
                <ProtectedRoute>
                  <BuilderDetails />
                </ProtectedRoute>
              }
            />
            <Route
              path="/workspaces/:workspaceId/projects"
              element={
                <ProtectedRoute>
                  <Projects />
                </ProtectedRoute>
              }
            />
            <Route
              path="/workspaces/:workspaceId/projects/:projectId"
              element={
                <ProtectedRoute>
                  <ProjectDetails />
                </ProtectedRoute>
              }
            />
            <Route
              path="/workspaces/:workspaceId/subdivisions"
              element={
                <ProtectedRoute>
                  <Subdivisions />
                </ProtectedRoute>
              }
            />
            <Route
              path="/workspaces/:workspaceId/models"
              element={
                <ProtectedRoute>
                  <Models />
                </ProtectedRoute>
              }
            />
            <Route
              path="/workspaces/:workspaceId/units"
              element={
                <ProtectedRoute>
                  <Units />
                </ProtectedRoute>
              }
            />
            <Route
              path="/workspaces/:workspaceId/common-areas"
              element={
                <ProtectedRoute>
                  <CommonAreas />
                </ProtectedRoute>
              }
            />
            <Route
              path="/workspaces/:workspaceId/parking"
              element={
                <ProtectedRoute>
                  <Parking />
                </ProtectedRoute>
              }
            />
            <Route
              path="/workspaces/:workspaceId/lockers"
              element={
                <ProtectedRoute>
                  <Lockers />
                </ProtectedRoute>
              }
            />
            <Route
              path="/workspaces/:workspaceId/bicycles"
              element={
                <ProtectedRoute>
                  <Bicycles />
                </ProtectedRoute>
              }
            />
            <Route
              path="/workspaces/:workspaceId/contacts"
              element={
                <ProtectedRoute>
                  <Contacts />
                </ProtectedRoute>
              }
            />
            <Route
              path="/workspaces/:workspaceId/deals"
              element={
                <ProtectedRoute>
                  <Deals />
                </ProtectedRoute>
              }
            />
            <Route
              path="/workspaces/:workspaceId/color-collections"
              element={
                <ProtectedRoute>
                  <ColorCollections />
                </ProtectedRoute>
              }
            />
            <Route
              path="/workspaces/:workspaceId/locations"
              element={
                <ProtectedRoute>
                  <PackageLocations />
                </ProtectedRoute>
              }
            />
            <Route
              path="/workspaces/:workspaceId/packages"
              element={
                <ProtectedRoute>
                  <Packages />
                </ProtectedRoute>
              }
            />
            <Route
              path="/workspaces/:workspaceId/options"
              element={
                <ProtectedRoute>
                  <Options />
                </ProtectedRoute>
              }
            />
            <Route
              path="/workspaces/:workspaceId/service/dashboard"
              element={
                <ProtectedRoute>
                  <ServiceDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/workspaces/:workspaceId/service/documents"
              element={
                <ProtectedRoute>
                  <ServiceDocuments />
                </ProtectedRoute>
              }
            />
            <Route
              path="/workspaces/:workspaceId/service/tarion"
              element={
                <ProtectedRoute>
                  <Tarion />
                </ProtectedRoute>
              }
            />
            <Route
              path="/workspaces/:workspaceId/service/deficiencies"
              element={
                <ProtectedRoute>
                  <Deficiencies />
                </ProtectedRoute>
              }
            />
            <Route
              path="/workspaces/:workspaceId/service/construction-status"
              element={
                <ProtectedRoute>
                  <ConstructionStatus />
                </ProtectedRoute>
              }
            />
            <Route
              path="/workspaces/:workspaceId/service/inspection"
              element={
                <ProtectedRoute>
                  <Inspection />
                </ProtectedRoute>
              }
            />
            <Route
              path="/workspaces/:workspaceId/inspection/:inspectionId/walkthrough"
              element={
                <ProtectedRoute>
                  <InspectionWalkthrough />
                </ProtectedRoute>
              }
            />
            <Route
              path="/workspaces/:workspaceId/service/checklist"
              element={
                <ProtectedRoute>
                  <Checklist />
                </ProtectedRoute>
              }
            />
            <Route
              path="/workspaces/:workspaceId/service/settings"
              element={
                <ProtectedRoute>
                  <ServiceSettings />
                </ProtectedRoute>
              }
            />

            {/* Purchaser Portal Routes */}
            <Route
              path="/workspaces/:workspaceId/purchaser-portal/project-progress"
              element={
                <ProtectedRoute>
                  <ProjectProgress />
                </ProtectedRoute>
              }
            />
            <Route
              path="/workspaces/:workspaceId/purchaser-portal/home-owners"
              element={
                <ProtectedRoute>
                  <HomeOwners />
                </ProtectedRoute>
              }
            />
            <Route
              path="/workspaces/:workspaceId/purchaser-portal/unit-deal"
              element={
                <ProtectedRoute>
                  <UnitDeal />
                </ProtectedRoute>
              }
            />
            <Route
              path="/workspaces/:workspaceId/purchaser-portal/customization"
              element={
                <ProtectedRoute>
                  <Customization />
                </ProtectedRoute>
              }
            />
            <Route
              path="/workspaces/:workspaceId/purchaser-portal/portal-admins"
              element={
                <ProtectedRoute>
                  <PortalAdmins />
                </ProtectedRoute>
              }
            />
            <Route
              path="/workspaces/:workspaceId/purchaser-portal/settings"
              element={
                <ProtectedRoute>
                  <PortalSettings />
                </ProtectedRoute>
              }
            />

            {/* Security Routes */}
            <Route
              path="/workspaces/:workspaceId/security/users"
              element={
                <ProtectedRoute>
                  <Users />
                </ProtectedRoute>
              }
            />
            <Route
              path="/workspaces/:workspaceId/security/roles"
              element={
                <ProtectedRoute>
                  <Roles />
                </ProtectedRoute>
              }
            />
            <Route
              path="/workspaces/:workspaceId/security/settings"
              element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              }
            />

            {/* Default Redirect */}
            <Route path="/" element={<Navigate to="/workspaces" replace />} />
            <Route path="*" element={<Navigate to="/workspaces" replace />} />
          </Routes>
        </WorkspaceProvider>
      </AuthProvider>
    </BrowserRouter>
  );
};

export default App;
